import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LoadResponse } from "../../../../models/api/load";
import { EmployeeGroupDTO } from "../../../../models/employee-groups";
import { TnAExtraHoursRef } from "../../../../models/tna";
import { TnADayDataRef } from "../../../../models/tna/tna-data/tna-day-data-ref";
import { TnAExtraHoursOverviewDTO } from "../../../../models/tna-extra-hours-overviews";
import { exportTnAToExcel } from "../../../../utils/export-presence-and-leaves";
import {
  getDayAndMonth,
  getDaysInMonth,
  getNormaleDateOrMonth,
  intlFormatDateShort,
  intlFormatDateWithHour,
} from "../../../../utils/time-utils";
import { monthsValues } from "../../../CustomDropdowns/CustomDropdownMonthsValues/CustomDropdownMonthsValues";
import ExportToExcelDialog from "../../ExportToExcelDialog/ExportToExcelDialog";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../utils";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnAExtraHoursData: (pageSize?: number) => Promise<LoadResponse<TnAExtraHoursOverviewDTO> | undefined>;
  allItems: () => TnAExtraHoursOverviewDTO[];
}

const ExportTnAExtraHoursToExcel = ({ disabled, loadEmployeeGroup, loadTnAExtraHoursData, allItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_extra_hours";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity },
    employeeGroup: { employeeGroupOption },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
    searchingDate: { year, month },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: ["Extra hours"],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`Report date ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Legal entities: ${!legalEntityOption?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Employee group: ${
              !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
        {
          data: [
            `Employee status: ${
              onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees ? "Only current employees" : "All employees"
            }`,
          ],
          fontSize: 12,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [`Period: ${year} ${t(monthsValues.find((currMonth) => currMonth.value === month)?.key ?? "")}`],
          fontSize: 14,
          isBold: true,
        },
      ];
    },
    [
      legalEntityOption?.legalEntityIds,
      selectedLegalEntity?.name,
      employeeGroupOption,
      onlyCurrentEmployeesOptionValue,
      year,
      month,
    ]
  );

  const headerNames = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);

    const dates = [];

    for (let i = 0; i < daysInMonth; i++) {
      dates.push(getDayAndMonth(new Date(year, month, i + 1)));
    }

    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t("common_words.employee")}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t("common_words.legal_entity")}`,
      `${t("common_words.tna_employee_group")}`,
      `${t(`${translationFields}.ytd`)}`,
      `${t(`${translationFields}.balance`)}`,
      ...dates,
    ];
  }, [year, month]);

  const dataToExportAsExcel = useCallback(
    (allItems: TnAExtraHoursOverviewDTO[]) => {
      const daysInMonth = getDaysInMonth(year, month);

      return allItems.map((item) => {
        const dates: { [key: string]: { value: string } } = {};

        const balance = item.daysData
          ?.map((day: TnADayDataRef) =>
            day?.extraHours?.reduce((acc, curr) => acc + (!curr.isRejected && !curr.isCancelled ? curr?.amount : 0), 0)
          )
          .reduce((acc: number, curr: number) => acc + curr, 0);

        for (let i = 0; i < daysInMonth; i++) {
          const currDay = item?.daysData?.find((day: TnADayDataRef) => day?.tnADate?.day === i + 1);
          const extraHoursTime = currDay?.extraHours?.reduce(
            (acc: number, curr: TnAExtraHoursRef) => acc + (!curr.isRejected && !curr.isCancelled ? curr?.amount : 0),
            0
          );

          const eh = currDay?.extraHours
            ?.map(({ amount }: { amount: number }) => `${amount < 0 ? "" : "+"}${Number(amount).toFixed(2)}h`)
            .join(", ")
            .trim();

          dates[getDayAndMonth(new Date(year, month, i + 1))] = { value: !!extraHoursTime && !!eh ? `${eh}` : " " };
        }

        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t("common_words.employee")}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t("common_words.legal_entity")}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t("common_words.tna_employee_group")}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
          [`${t(`${translationFields}.ytd`)}`]: {
            value: `${item.extraHoursYTD ? item.extraHoursYTD.toFixed(2) : " "}`,
          },
          [`${t(`${translationFields}.balance`)}`]: { value: `${balance ? balance.toFixed(2) : " "}` },
          ...dates,
          sortDirection: headerNames,
        };
      });
    },
    [headerNames, year, month]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        exportTnAToExcel(
          dataToExportAsExcel(allItems()),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Extra hours`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (option === "exportAllData") {
        const employeeGroup = employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const firstResponse = await loadTnAExtraHoursData();

        if (firstResponse?.error) return;

        const allTnAExtraHoursData = await loadTnAExtraHoursData(firstResponse?.totalItems);

        if (!!allTnAExtraHoursData?.error || !allTnAExtraHoursData?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allTnAExtraHoursData.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Extra hours`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      loadTnAExtraHoursData,
    ]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportTnAExtraHoursToExcel;
